/**
 * A single-select with a type-ahead filter, for lists too long to scan.
 *
 * A native <select> is the right control until the list outgrows the eye: by a
 * few dozen speakers, finding a name means scrolling a popup with no search.
 * This keeps the single-selection semantics and adds a filter at the top, with
 * the list itself scrolled to a fixed number of visible rows so the panel does
 * not grow without limit as the roster does.
 *
 * Deliberately not a headless-UI dependency: the behaviour needed here is a
 * filter, arrow keys, Enter, and Escape, and those are cheaper to own than a
 * library's opinions about portals and focus scopes.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

export type SearchableOption = {
  value: string;
  label: string;
  /** Second line, and also matched by the filter — an event date, an org. */
  hint?: string;
  /**
   * Listed but not choosable.
   *
   * For a row that belongs in the list yet cannot be picked — a member the
   * campaign cannot reach, say. Hiding it would leave someone searching for a
   * name that is plainly in the community and finding nothing; showing it greyed,
   * with the hint saying why, answers the question instead.
   */
  disabled?: boolean;
};

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Type to filter…",
  emptyLabel = "No matches",
  visibleRows = 5,
  id,
  ariaLabel,
  disabled,
}: {
  options: SearchableOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** How many rows fit before the list scrolls. */
  visibleRows?: number;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find(option => option.value === value) ?? null;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(needle) || option.hint?.toLowerCase().includes(needle),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    // Focus goes to the filter, which is the whole point of opening this.
    window.requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Keeps the highlighted row inside the scroll window as the arrows move it.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelectorAll<HTMLElement>("[data-option]")[activeIndex];
    row?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const commit = (option: SearchableOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex(current => {
        const next = current + (event.key === "ArrowDown" ? 1 : -1);
        return ((next % filtered.length) + filtered.length) % filtered.length;
      });
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) commit(option);
    }
  };

  return (
    <div ref={rootRef} className="chi-searchable" style={{ position: "relative" }} onKeyDown={onKeyDown}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(current => !current)}
        className="chi-searchable__trigger"
      >
        <span className="chi-searchable__value" data-placeholder={!selected || undefined}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} aria-hidden="true" data-open={open || undefined} />
      </button>

      {open && (
        <div className="chi-searchable__panel">
          <div className="chi-searchable__search">
            <Search size={13} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={event => { setQuery(event.target.value); setActiveIndex(0); }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>

          <div
            ref={listRef}
            role="listbox"
            className="chi-searchable__list"
            /* Height is set from the row count rather than a pixel value, so the
               window still shows the intended number of rows if the row height
               changes. */
            style={{ maxHeight: `calc(${visibleRows} * 2.6rem)` }}
          >
            {filtered.length === 0 ? (
              <p className="chi-searchable__empty">{emptyLabel}</p>
            ) : (
              filtered.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  data-option
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled || undefined}
                  data-active={index === activeIndex || undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(option)}
                  style={option.disabled ? { opacity: .5, cursor: "not-allowed" } : undefined}
                >
                  <span>
                    <strong>{option.label}</strong>
                    {option.hint && <em>{option.hint}</em>}
                  </span>
                  {option.value === value && <Check size={13} aria-hidden="true" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <style>{`
        .chi-searchable__trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: .5rem;
          width: 100%;
          min-height: 42px;
          padding: 0 .7rem;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: .55rem;
          background: rgba(255,255,255,.04);
          color: #F8FAFC;
          font-family: 'Inter', sans-serif;
          font-size: .8rem;
          text-align: left;
          cursor: pointer;
        }

        .chi-searchable__trigger:disabled { opacity: .5; cursor: not-allowed; }
        .chi-searchable__trigger svg { flex-shrink: 0; color: #64748B; transition: transform .16s ease; }
        .chi-searchable__trigger svg[data-open] { transform: rotate(180deg); }

        .chi-searchable__value {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .chi-searchable__value[data-placeholder] { color: #64748B; }

        .chi-searchable__panel {
          position: absolute;
          top: calc(100% + .3rem);
          left: 0;
          right: 0;
          z-index: 60;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .6rem;
          background: #0D1220;
          box-shadow: 0 18px 44px rgba(0,0,0,.5);
          overflow: hidden;
        }

        .chi-searchable__search {
          display: flex;
          align-items: center;
          gap: .45rem;
          padding: .5rem .6rem;
          border-bottom: 1px solid rgba(255,255,255,.08);
          color: #64748B;
        }

        .chi-searchable__search input {
          flex: 1;
          min-width: 0;
          border: 0;
          background: none;
          outline: none;
          color: #F8FAFC;
          font-family: 'Inter', sans-serif;
          font-size: .78rem;
        }

        .chi-searchable__search input::placeholder { color: #55637A; }

        .chi-searchable__list { overflow-y: auto; padding: .25rem; }

        .chi-searchable__list button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: .5rem;
          width: 100%;
          padding: .4rem .5rem;
          border: 0;
          border-radius: .4rem;
          background: none;
          color: #CBD5E1;
          text-align: left;
          cursor: pointer;
        }

        .chi-searchable__list button[data-active] { background: rgba(0,212,255,.12); color: #F8FAFC; }
        .chi-searchable__list button svg { color: #7DEAFF; }

        .chi-searchable__list strong {
          display: block;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .76rem;
          font-weight: 650;
        }

        .chi-searchable__list em {
          display: block;
          color: #718096;
          font-family: 'Inter', sans-serif;
          font-size: .68rem;
          font-style: normal;
        }

        .chi-searchable__empty {
          margin: 0;
          padding: .7rem .6rem;
          color: #64748B;
          font-family: 'Inter', sans-serif;
          font-size: .76rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .chi-searchable__trigger svg { transition: none; }
        }
      `}</style>
    </div>
  );
}

export default SearchableSelect;
